/* walls.js — wall-drawing geometry for Step 1 (Walls), ported from lhc-app.html
 * (snapPoint ~36028, wall creation ~36543/36606/33581, wallDrawTool ~29427). Units = METRES.
 * Pure helpers; the state ops (addWall/addBox/clearWalls/removeWall) live in state.js.
 *
 * WI-12 plan-tool additions (same anchor style): pointToSegmentDist ~36322, findWallAt ~36332,
 * arcSweep ~37226, clipLineToRect (Liang–Barsky) ~35171, isPlanFeaturePoint ~36946,
 * guideAnchorFromClick ~36884, planGuidePreview ~36913 (+ recomputePlanGuideTyping ~36990),
 * typedDrawEnd ~35222/~37837, getBackWall ~31195, getCabinetStartX ~31124,
 * getBackWallLengthM ~31207, dragWallEndpoint/dragWallBody (gap feature — the prototype only
 * renders .wall-handle squares with no drag; this fills that gap without changing wall model).
 *
 * wall model: { id, start:[x,z], end:[x,z], height, style?, thickness?, type?, center?, radius?, midpoint?, ccw?, hatch?, studSpacing? }
 * wallDrawTool defaults (~29427): { snapToGrid:true, gridSize:0.05 (5cm), snapAngle:true }
 */
import { DEFAULT_WALL_HEIGHT, WALL_THICKNESS, quantPt } from "./constants";
// Function-level-only cycle (walls → snapping → wallJoins → walls) — safe: no module
// evaluates the other's exports at load time, only inside function bodies.
import { wallInnerSpan } from "./snapping";

export const WALL_DRAW_DEFAULTS = { snapToGrid: true, gridSize: 0.05, snapAngle: true };
const ENDPOINT_SNAP = 0.20; // m

let _seq = 0;
function wid(prefix) { _seq += 1; return prefix + _seq; }

export function wallLength(w) { return Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]); }
export function wallAngleDeg(w) { return (Math.atan2(w.end[1] - w.start[1], w.end[0] - w.start[0]) * 180) / Math.PI; }

/** snapPoint(p, walls, opts): endpoint snap (0.20m) → angle snap (45°, from startPoint) → grid
 *  snap (gridSize). (~36028) opts = { snapToGrid, gridSize, snapAngle, startPoint }. */
export function snapPoint(p, walls, opts = WALL_DRAW_DEFAULTS) {
  if (!opts.snapToGrid) return [p[0], p[1]];
  let x = p[0], z = p[1];
  // opts.snapEndpoints === false disables the 0.20m CENTERLINE-endpoint tier
  // (2026-08-21 wall-connect fix: the draw-flow start fallback must never pull
  // the click onto a centerline end — the corner tier owns wall-attached
  // starts; every legacy caller keeps the tier by default).
  if (opts.snapEndpoints !== false) {
    let bestEp = null, bestEpDist = ENDPOINT_SNAP;
    for (const w of walls || []) {
      const ds = Math.hypot(x - w.start[0], z - w.start[1]);
      if (ds < bestEpDist) { bestEpDist = ds; bestEp = w.start; }
      const de = Math.hypot(x - w.end[0], z - w.end[1]);
      if (de < bestEpDist) { bestEpDist = de; bestEp = w.end; }
    }
    if (bestEp) return [bestEp[0], bestEp[1]];
  }
  if (opts.snapAngle && opts.startPoint) {
    const sx = opts.startPoint[0], sz = opts.startPoint[1];
    const dx = x - sx, dz = z - sz;
    const len = Math.hypot(dx, dz);
    if (len > 0.01) {
      const step = Math.PI / 4;
      const snapAng = Math.round(Math.atan2(dz, dx) / step) * step;
      x = sx + Math.cos(snapAng) * len;
      z = sz + Math.sin(snapAng) * len;
    }
  }
  const g = opts.gridSize || 0.05;
  return [Math.round(x / g) * g, Math.round(z / g) * g];
}

/** A straight wall (Line / Chained). (~36543)
 *  Endpoints are quantized to the 0.1mm storage quantum (Phase-8): committed
 *  geometry is drift-free, so coincident joints stay byte-identical. */
export function makeWall(start, end, height = DEFAULT_WALL_HEIGHT) {
  return { id: wid("w-manual-"), start: quantPt(start), end: quantPt(end), height };
}

/** Four walls forming a closed rectangle between two opposite corners (Box). */
export function makeBox(c1, c2, height = DEFAULT_WALL_HEIGHT) {
  const x1 = c1[0], z1 = c1[1], x2 = c2[0], z2 = c2[1];
  const corners = [[x1, z1], [x2, z1], [x2, z2], [x1, z2]];
  const out = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i], b = corners[(i + 1) % 4];
    out.push({ id: wid("w-box-"), start: [a[0], a[1]], end: [b[0], b[1]], height });
  }
  return out;
}

/** An arc wall (curve is render-only; placement uses the chord — see placement.js). (~36606) */
export function makeArc(A, B, center, radius, midpoint, ccw, height = DEFAULT_WALL_HEIGHT) {
  return { id: wid("w-arc-"), start: [A[0], A[1]], end: [B[0], B[1]], height, type: "arc", center: [center[0], center[1]], radius, midpoint: [midpoint[0], midpoint[1]], ccw: !!ccw };
}

/** Circle through 3 points → {center,[x,z], radius}, or null if collinear. */
export function circleFrom3Points(a, b, c) {
  const ax = a[0], az = a[1], bx = b[0], bz = b[1], cx = c[0], cz = c[1];
  const d = 2 * (ax * (bz - cz) + bx * (cz - az) + cx * (az - bz));
  if (Math.abs(d) < 1e-9) return null;
  const a2 = ax * ax + az * az, b2 = bx * bx + bz * bz, c2 = cx * cx + cz * cz;
  const ux = (a2 * (bz - cz) + b2 * (cz - az) + c2 * (az - bz)) / d;
  const uz = (a2 * (cx - bx) + b2 * (ax - cx) + c2 * (bx - ax)) / d;
  return { center: [ux, uz], radius: Math.hypot(ax - ux, az - uz) };
}

/** Arc wall from start, end and a through-point (the original's 3-click Arc). Falls back to a
 *  straight wall if the 3 points are collinear. ccw from the winding of (start→through→end). */
export function makeArcFrom3(p0, p1, through, height = DEFAULT_WALL_HEIGHT) {
  const circ = circleFrom3Points(p0, through, p1);
  if (!circ) return makeWall(p0, p1, height);
  const ccw = ((through[0] - p0[0]) * (p1[1] - p0[1]) - (through[1] - p0[1]) * (p1[0] - p0[0])) > 0;
  return makeArc(p0, p1, circ.center, circ.radius, through, ccw, height);
}

/* ── WI-12 plan tool helpers ─────────────────────────────────────────────── */

/** Distance from point p to segment a→b. (~36322) */
export function pointToSegmentDist(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-9) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const nx = a[0] + t * dx, ny = a[1] + t * dy;
  return Math.hypot(p[0] - nx, p[1] - ny);
}

/** Signed arc sweep (radians, + = ccw). (~37226) */
export function arcSweep(wall) {
  const [cx, cz] = wall.center;
  const angA = Math.atan2(wall.start[1] - cz, wall.start[0] - cx);
  const angB = Math.atan2(wall.end[1] - cz, wall.end[0] - cx);
  const norm = (a, ref) => { let x = a - ref; while (x < 0) x += Math.PI * 2; return x; };
  return wall.ccw ? norm(angB, angA) : -norm(angA, angB);
}

/** Annulus-band geometry for an arc wall in WORLD space (renderPlanSvg ~34398).
 *  outer/inner radii = r ± t/2; endpoints at angA/angB; SVG sweep flags account
 *  for the world-z → screen-y flip (a +ve/CCW world sweep draws CW → flag 1, and
 *  the inner arc reverses B→A so its flag is opposite). Projection is the caller's
 *  job (returns world coords). Returns null for a degenerate sweep. */
export function arcBandGeometry(wall, thickness = WALL_THICKNESS) {
  const [cx, cz] = wall.center;
  const r = wall.radius || 0;
  const angA = Math.atan2(wall.start[1] - cz, wall.start[0] - cx);
  const angB = Math.atan2(wall.end[1] - cz, wall.end[0] - cx);
  const sweep = arcSweep(wall);
  if (Math.abs(sweep) < 1e-6) return null;
  const outR = r + thickness / 2, inR = Math.max(0.001, r - thickness / 2);
  const at = (rad, ang) => [cx + rad * Math.cos(ang), cz + rad * Math.sin(ang)];
  const angMid = angA + sweep / 2;
  return {
    outR, inR, sweep,
    oA: at(outR, angA), oB: at(outR, angB), iA: at(inR, angA), iB: at(inR, angB),
    largeArc: Math.abs(sweep) > Math.PI ? 1 : 0,
    sweepFlagOuter: sweep > 0 ? 1 : 0,
    sweepFlagInner: sweep > 0 ? 0 : 1,
    numAngle: angMid, numPoint: at(r, angMid),
  };
}

/** findWallAt(p, walls): nearest wall within 0.15 m (arc band-aware) or null. (~36332) */
export function findWallAt(p, walls) {
  let best = null, bestDist = 0.15;
  (walls || []).forEach((w) => {
    if (w.type === "arc") {
      const [cx, cz] = w.center;
      const distFromCenter = Math.hypot(p[0] - cx, p[1] - cz);
      const bandHalf = 0.10;
      const radialErr = Math.abs(distFromCenter - w.radius);
      if (radialErr > bandHalf + 0.05) return;
      const angP = Math.atan2(p[1] - cz, p[0] - cx);
      const angA = Math.atan2(w.start[1] - cz, w.start[0] - cx);
      const sweep = arcSweep(w);
      const norm = (a, ref) => { let x = a - ref; while (x < 0) x += Math.PI * 2; return x; };
      const tParam = (sweep >= 0) ? norm(angP, angA) / sweep : -norm(angA, angP) / sweep;
      if (tParam < 0 || tParam > 1) return;
      if (radialErr < bestDist) { best = w; bestDist = radialErr; }
      return;
    }
    const d = pointToSegmentDist(p, w.start, w.end);
    if (d < bestDist) { best = w; bestDist = d; }
  });
  return best;
}

/** Liang–Barsky: clip the infinite line through p along dir to rect {x0,z0,x1,z1}.
 *  Returns [[ax,az],[bx,bz]] or null. (~35171) */
export function clipLineToRect(p, dir, rect) {
  let t0 = -1e9, t1 = 1e9;
  const clip = (num, den) => {
    if (Math.abs(den) < 1e-12) return num >= 0;
    const r = num / den;
    if (den < 0) { if (r > t1) return false; if (r > t0) t0 = r; }
    else { if (r < t0) return false; if (r < t1) t1 = r; }
    return true;
  };
  if (clip(p[0] - rect.x0, dir[0]) && clip(rect.x1 - p[0], -dir[0]) &&
      clip(p[1] - rect.z0, dir[1]) && clip(rect.z1 - p[1], -dir[1])) {
    return [[p[0] + t0 * dir[0], p[1] + t0 * dir[1]], [p[0] + t1 * dir[0], p[1] + t1 * dir[1]]];
  }
  return null;
}

/** True if p coincides with a wall start/end point or a guide-line crossing. (~36946)
 *  guides = [{ p:[x,z], dir:[dx,dz] }] (infinite construction lines). */
export function isPlanFeaturePoint(p, walls, guides = []) {
  const TOL = 0.012;
  for (const w of walls || []) {
    if (Math.hypot(p[0] - w.start[0], p[1] - w.start[1]) < TOL) return true;
    if (Math.hypot(p[0] - w.end[0], p[1] - w.end[1]) < TOL) return true;
  }
  const gs = guides || [];
  for (let i = 0; i < gs.length; i++)
    for (let j = i + 1; j < gs.length; j++) {
      const det = gs[i].dir[0] * (-gs[j].dir[1]) - gs[i].dir[1] * (-gs[j].dir[0]);
      if (Math.abs(det) < 1e-9) continue;
      const t = ((gs[j].p[0] - gs[i].p[0]) * (-gs[j].dir[1]) - (gs[j].p[1] - gs[i].p[1]) * (-gs[j].dir[0])) / det;
      const ix = gs[i].p[0] + t * gs[i].dir[0], iz = gs[i].p[1] + t * gs[i].dir[1];
      if (Math.hypot(p[0] - ix, p[1] - iz) < TOL) return true;
    }
  return false;
}

/** Plan-guide first click (~36884): a click that lands mid-wall (away from feature points)
 *  anchors a PARALLEL offset guide on that wall's line; otherwise a free guide at the snapped
 *  point. `snapped` = snapPoint output, `raw` = unsnapped world point (wall hit uses raw).
 *  Returns { anchor:[x,z], refDir:[dx,dz]|null }. */
export function guideAnchorFromClick(snapped, raw, walls, guides = []) {
  const atFeature = isPlanFeaturePoint(snapped, walls, guides);
  const hit = atFeature ? null : findWallAt(raw, walls);
  if (hit && hit.type !== "arc") {
    const dx = hit.end[0] - hit.start[0], dz = hit.end[1] - hit.start[1];
    const L = Math.hypot(dx, dz) || 1;
    return { anchor: [hit.start[0], hit.start[1]], refDir: [dx / L, dz / L] };
  }
  return { anchor: [snapped[0], snapped[1]], refDir: null };
}

/** Live plan-guide preview (~36913; typed-mm recompute ~36990). refDir set → parallel offset
 *  guide at the signed perpendicular distance from the anchored wall line (typing overrides
 *  the magnitude, sign follows the cursor side). refDir null → free guide through the anchor:
 *  exactly through a feature point at any angle, else 45°-snapped. Returns {p,dir,dist}|null. */
export function planGuidePreview(anchor, refDir, typing, rawCursor, walls, guides = []) {
  if (!anchor) return null;
  const cur = snapPoint(rawCursor, walls || []);
  if (refDir) {
    const dir = refDir;
    const ax = anchor[0], az = anchor[1];
    const nx = -dir[1], nz = dir[0];
    let dist = (cur[0] - ax) * nx + (cur[1] - az) * nz; // signed perpendicular
    const sign = dist < 0 ? -1 : 1;
    if (typing) {
      const v = parseFloat(typing);
      if (!isNaN(v)) dist = sign * Math.abs(v) / 1000; // typed mm → m
    }
    return { p: [ax + nx * dist, az + nz * dist], dir: dir.slice(), dist: Math.abs(dist) };
  }
  const dx = cur[0] - anchor[0], dz = cur[1] - anchor[1];
  const L = Math.hypot(dx, dz);
  if (L < 0.03) return null;
  if (isPlanFeaturePoint(cur, walls, guides)) {
    return { p: anchor.slice(), dir: [dx / L, dz / L], dist: null };
  }
  const step = Math.PI / 4;
  const sa = Math.round(Math.atan2(dz, dx) / step) * step;
  return { p: anchor.slice(), dir: [Math.cos(sa), Math.sin(sa)], dist: null };
}

/** Draw-by-typing endpoint (~35222 preview lock / ~37837 Enter commit): lock the preview end
 *  to exactly `typing` mm from start along the current pull direction. Null when unusable
 *  (no direction yet / non-numeric / zero). Callers enforce the 100 mm commit minimum. */
export function typedDrawEnd(start, previewEnd, typing) {
  const typedMm = typing ? parseFloat(typing) : NaN;
  if (!isFinite(typedMm) || typedMm <= 0 || !start || !previewEnd) return null;
  const dx = previewEnd[0] - start[0], dz = previewEnd[1] - start[1];
  const d = Math.hypot(dx, dz);
  if (d < 1e-6) return null;
  const L = typedMm / 1000;
  return [start[0] + (dx / d) * L, start[1] + (dz / d) * L];
}

/** Back wall = lowest-z horizontal wall. (~31195) */
export function getBackWall(walls) {
  if (!walls || walls.length === 0) return null;
  let back = null, backZ = Infinity;
  walls.forEach((w) => {
    const isHorizontal = Math.abs(w.start[1] - w.end[1]) < 0.05;
    if (!isHorizontal) return;
    const mz = (w.start[1] + w.end[1]) / 2;
    if (mz < backZ) { backZ = mz; back = w; }
  });
  return back;
}

/** Dimension-chain point 0 (LOCK-D-01: the PHYSICAL face corner, not a centerline node).
 *  Now derived from the SAME signed span every clamp uses (wallInnerSpan): concave corner
 *  → the perpendicular wall's inner face (+t/2); convex corner → the visible outer band
 *  corner (−t/2, PAST the node); open end → the node. One reference for placement,
 *  snapping, collision and dimensions — no drift. (~31124)
 *  Null when there is no back wall (the wall-less legacy fallback is WI-13's placement.js). */
export function getCabinetStartX(walls) {
  const wall = getBackWall(walls);
  if (!wall) return null;
  const backMinX = Math.min(wall.start[0], wall.end[0]);
  const sp = wallInnerSpan(wall, walls);
  // off0 applies at wall.start — map the signed offsets onto the min-X end.
  const offAtMin = wall.start[0] <= wall.end[0] ? sp.off0 : sp.off1;
  return backMinX + offAtMin;
}

/** Usable back-wall run p0→pEnd (physical face corner → physical face corner). (~31207) */
export function getBackWallLengthM(walls) {
  const w = getBackWall(walls);
  if (!w) return 0;
  return Math.max(0, wallInnerSpan(w, walls).innerLen);
}

/** Wall endpoint drag (gap feature — see header): new position for wall.start/end, snapped
 *  via snapPoint against the OTHER walls (join corners) with the fixed endpoint as the angle
 *  reference. Returns [x,z]; does NOT mutate. */
export function dragWallEndpoint(wall, which, world, walls) {
  const others = (walls || []).filter((w) => w.id !== wall.id);
  const fixed = which === "start" ? wall.end : wall.start;
  return snapPoint(world, others, { snapToGrid: true, gridSize: 0.05, snapAngle: true, startPoint: fixed });
}

/** Wall body drag (gap feature): translate the whole wall by delta, grid-snapping the moved
 *  start so length/direction are preserved exactly. Returns { start, end }; does NOT mutate. */
export function dragWallBody(origStart, origEnd, delta, gridSize = 0.05) {
  const g = gridSize || 0.05;
  const nsx = Math.round((origStart[0] + delta[0]) / g) * g;
  const nsz = Math.round((origStart[1] + delta[1]) / g) * g;
  return {
    start: [nsx, nsz],
    end: [nsx + (origEnd[0] - origStart[0]), nsz + (origEnd[1] - origStart[1])],
  };
}

/** wallsForRoomShape(shape, {width,depth,height}) — the wizard's room presets, ported verbatim
 *  (~33575). Centered on the origin; back wall at z=-halfD. (~33614 applyRoomShape = walls=this). */
export function wallsForRoomShape(shape, opts = {}) {
  const W = opts.width || 6.0;
  const D = opts.depth || 4.0;
  const H = opts.height || DEFAULT_WALL_HEIGHT;
  // 2026-07-19: the wizard's Room step wall thickness flows into presets
  // (per-wall `thickness`); absent → the verbatim 0.15 default, records
  // byte-identical to the original port (no thickness key).
  const t = opts.thickness || 0.15; // WALL_THICKNESS
  const half = (W - t) / 2;
  const halfD = (D - t) / 2;
  const mk = (id, sx, sz, ex, ez) => ({ id, start: [sx, sz], end: [ex, ez], height: H, ...(opts.thickness ? { thickness: opts.thickness } : {}) });
  const backWall = mk("w1", -half, -halfD, half, -halfD);
  const leftWall = mk("w2", -half, -halfD, -half, halfD);
  const rightWall = mk("w3", half, -halfD, half, halfD);
  if (shape === "straight") return [backWall];
  if (shape === "l-left") return [backWall, leftWall];
  if (shape === "l-right") return [backWall, rightWall];
  if (shape === "u-shape") return [backWall, leftWall, rightWall];
  if (shape === "rect" || shape === "rectangle") {
    const frontWall = mk("w4", -half, halfD, half, halfD);
    return [backWall, rightWall, frontWall, leftWall];
  }
  return [];
}
